import { useMemo, useState } from 'react';

import { ComponentTable } from 'approot/activity-builder/components/component.constants';
import { ErrorMessageActionable } from 'approot/shared/error-message/error-message-actionable';
import Bouncer from 'approot/shared/bouncer/bouncer';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import { useGetStudentAnswersPollingFallback } from '../use-get-student-answers';
import { useGetClassStudents } from '../classroom-student.hooks';
import SubActivityView from 'approot/activity-builder/components/sub-activity/sub-activity-view';
import {
  ClassroomAnswerGrid,
  ClassroomAnswerGridButton,
  ClassroomAnswerGridInner,
  ClassroomAnswerGridItem,
  ClassroomAnswerInstruction,
} from '../classroom-answer.style';
import { ClassAnswerNoStudents } from '../class-answer-empty';
import {
  getStudentTableAnswers,
  hasStudentTableAnswer,
} from './table-response-individual.utils';
import TableReadonlyAnswers from 'approot/activity-builder/components/table/table-readonly-answers';
import ActivityBuilderComponentPreview from 'approot/activity-builder/page/activity-builder-component-preview';
import { TableWrapper } from 'approot/activity-builder/components/table/table-check-answer.style';
import { ScreenReaderOnly } from 'approot/shared/styles/global.styles';
import ClassroomAwaitingResponse from '../classroom-awaiting';
import ClassroomAnswerDetailHeader from '../classroom-answer-detail-header';
import ClassroomAnswerStudentVisibility from '../classroom-answer-student-visibility';
import { ClassroomStudentIdentifierWithVisibility } from '../classroom-student-identifier';
import { useActivitySheetScrollReset } from 'approot/activity-builder/components/shared/sheet/use-activity-sheet-scroll-reset';
import { ActivitySheetInnerMax } from 'approot/activity-builder/components/shared/sheet/activity-sheet.style';
import { Box } from 'approot/shared/design-system/box/box.style';

type Props = {
  component: Omit<ComponentTable, '__typename'>;
  width: number;
};

const TableResponseIndividual = ({ component, width }: Props) => {
  const [activeStudent, setActiveStudent] = useState<number>(0);

  const { userClassId } = useActivityBuilderPresentContext();

  const {
    data: studentsData,
    loading: studentsLoading,
    error: studentsError,
  } = useGetClassStudents({
    classId: Number(userClassId) || 1,
  });

  const { data, error: studentAnswersError } =
    useGetStudentAnswersPollingFallback({
      userClassId: Number(userClassId),
      componentId: component.id,
    });

  const studentsInClass = useMemo(
    () => studentsData?.getClassStudents || [],
    [studentsData]
  );

  const studentAnswers = useMemo(
    () => data?.getClassStudentsAnswers || [],
    [data]
  );

  const results = useMemo(() => {
    const students =
      activeStudent > 0
        ? studentsInClass.filter(s => s.id === activeStudent)
        : studentsInClass;

    return getStudentTableAnswers({
      studentAnswers,
      studentsInClass: students,
    });
  }, [activeStudent, studentsInClass, studentAnswers]);

  useActivitySheetScrollReset(activeStudent);

  if (studentsLoading || !studentAnswers) {
    return <Bouncer />;
  }

  if (studentAnswersError || studentsError) {
    return <ErrorMessageActionable ariaPage="Table responses" paddingTop={0} />;
  }

  if (studentsInClass.length === 0) {
    return <ClassAnswerNoStudents />;
  }

  return (
    <ClassroomAnswerInstruction>
      {activeStudent > 0 ? (
        <ActivitySheetInnerMax>
          <ClassroomAnswerDetailHeader
            studentId={activeStudent}
            students={studentsInClass}
            onSelect={setActiveStudent}
          />
          <SubActivityView subActivityDetails={component.subActivityProperties}>
            {hasStudentTableAnswer(results[0]) ? (
              <TableWrapper $maxWidth={width}>
                <TableReadonlyAnswers
                  {...component}
                  answers={results[0].answers}
                />
              </TableWrapper>
            ) : (
              <ClassroomAwaitingResponse />
            )}
          </SubActivityView>
        </ActivitySheetInnerMax>
      ) : (
        <SubActivityView subActivityDetails={component.subActivityProperties}>
          <Box $justifyContent="flex-end" $marginBottom={24}>
            <ClassroomAnswerStudentVisibility />
          </Box>
          <ClassroomAnswerGrid>
            {results.map(result => (
              <ClassroomAnswerGridItem key={result.studentId}>
                <ClassroomAnswerGridInner>
                  {hasStudentTableAnswer(result) ? (
                    <>
                      <ClassroomAnswerGridButton
                        type="button"
                        onClick={() => setActiveStudent(result.studentId)}
                      >
                        <ScreenReaderOnly>
                          {`View ${result.studentName}'s response`}
                        </ScreenReaderOnly>
                      </ClassroomAnswerGridButton>
                      <ActivityBuilderComponentPreview
                        content={{
                          width,
                          height: 232,
                        }}
                      >
                        <TableReadonlyAnswers
                          {...component}
                          answers={result.answers}
                        />
                      </ActivityBuilderComponentPreview>
                    </>
                  ) : (
                    <ClassroomAwaitingResponse />
                  )}
                </ClassroomAnswerGridInner>
                <ClassroomStudentIdentifierWithVisibility
                  name={result.studentName}
                  emoji={result.studentEmoji}
                />
              </ClassroomAnswerGridItem>
            ))}
          </ClassroomAnswerGrid>
        </SubActivityView>
      )}
    </ClassroomAnswerInstruction>
  );
};

export default TableResponseIndividual;
